import { useState } from 'react'
import {
  Box,
  Typography,
  Divider,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material'
import { NavLink } from 'react-router-dom'
import config from '../twh/config'

// ✅ Context
import { useAuth } from '../context/AuthContext'

// SVG icons
import iconNetworking from '../assets/icons/networking.svg'
import iconWebsite from '../assets/icons/website.svg'
import iconMarketing from '../assets/icons/marketing.svg'
import iconAngleDown from '../assets/icons/angle-down.svg'


function MenuMain({ icon, label, arrow, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.3,
        cursor: 'pointer',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <img src={icon} width={14} alt="" />
        <Typography fontSize={12}>{label}</Typography>
      </Box>
      {arrow && <img src={iconAngleDown} width={10} alt="" />}
    </Box>
  )
}

function SubItem({ label, to }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        textDecoration: 'none',
        color: isActive ? '#ff2c6d' : '#000',
      })}
    >
      <Typography
        sx={{
          py: 0.5,
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        {label}
      </Typography>
    </NavLink>
  )
}

export default function Sidebar({ open }) {

  // ✅ Context data
  const { userData, fetchUser } = useAuth()

  const accessLocations = userData?.location?.accessOther || []
  const selectedLocation = userData?.location?.selected || {}

  const [locationId, setLocationId] = useState(
    selectedLocation?.id ? String(selectedLocation.id) : ""
  )

  const [openMenu, setOpenMenu] = useState(null)

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity })
  }

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const selectedApiKey = selectedLocation?.apiKey || ""

  const handleCopyApiKey = () => {
    if (!selectedApiKey) return

    navigator.clipboard
      .writeText(selectedApiKey)
      .then(() => {
        showSnackbar('API Key copied successfully!', 'success')
      })
      .catch((err) => {
        console.error('Failed to copy API key:', err)
        showSnackbar('Failed to copy API key', 'error')
      })
  }

  const handleLocationChange = async (e) => {
    const newLocationId = e.target.value
    setLocationId(newLocationId)

    try {
      const formData = new FormData()
      formData.append('userSelectedLocationID', newLocationId)

      await fetch(
        config.reactApi_baseUrl + '/react-api/users/loginUser-chagneLocation.php',
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      )

     
      showSnackbar('Location changed successfully', 'success')
      
      
    } catch (error) {
      console.error('Failed to change location:', error)
      showSnackbar('Failed to change location', 'error')
    }

    window.location.reload()
  }

  if (!open) return null

  return (
    <Box
      sx={{
        width: '264px',
        position: 'fixed',
        top: '60px',
        left: 0,
        bottom: 0,
        backgroundColor: '#fff',
        fontSize: '12px',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography fontWeight={700} mb={1} fontSize={12}>
          Switch Between Locations
        </Typography>

        <Select
          fullWidth
          size="small"
          value={locationId}
          onChange={handleLocationChange}
          sx={{ mb: 1, fontSize: 12 }}
        >
          {accessLocations.map((loc) => (
            <MenuItem key={loc.id} value={String(loc.id)}>
              {loc.name}
            </MenuItem>
          ))}
        </Select>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography fontSize={10}>
            Api key: <b>{selectedApiKey || '—'}</b>
          </Typography>
          <Typography
            fontSize={10}
            sx={{ cursor: 'pointer', color: '#1976d2' }}
            onClick={handleCopyApiKey}
          >
            Copy
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography fontWeight={700} mb={1} fontSize={12}>
          Navigate to
        </Typography>

        <MenuMain
          icon={iconNetworking}
          label="Data insights"
          arrow
          onClick={() => setOpenMenu(openMenu === 'data' ? null : 'data')}
        />
        {openMenu === 'data' && (
          <Box sx={{ pl: 4, mb: 2 }}>
            <SubItem label="Website data" to="/website-data" />
            <SubItem label="Advertising data" to="/advertising" />
            <SubItem label="Performance per channel" to="/performance" />
          </Box>
        )}

        <MenuMain
          icon={iconWebsite}
          label="Website"
          arrow
          onClick={() => setOpenMenu(openMenu === 'website' ? null : 'website')}
        />
        {openMenu === 'website' && (
          <Box sx={{ pl: 4, mb: 2 }}>
            <SubItem label="Menu" to="/menu" />
            <SubItem label="PDF Menu" to="/file-menu" />
            <SubItem label="Opening hours" to="/opening-hours" />
            <SubItem label="Popups" to="/pop-up" />
            <SubItem label="Color Scheme" to="/color-scheme" />
            <SubItem label="Website settings" to="/settings" />
          </Box>
        )}

        <MenuMain
          icon={iconMarketing}
          label="Marketing"
          arrow
          onClick={() => setOpenMenu(openMenu === 'marketing' ? null : 'marketing')}
        />
        {openMenu === 'marketing' && (
          <Box sx={{ pl: 4, mb: 2 }}>
            <SubItem label="Review Manager" to="/marketing/review-manager" />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography fontWeight={700} mb={1} fontSize={12}>
          Settings
        </Typography>
        <SubItem label="Integrations" to="/integrations" />
        <SubItem label="Users" to="/users" />
        <SubItem label="Account Billing" to="/account-billing" />

        <Divider sx={{ my: 2 }} />

        <Typography fontWeight={700} mb={1} fontSize={12}>
          Support
        </Typography>
        <SubItem label="Frequently Asked Questions" to="/faq" />
        <SubItem label="Contact" to="/contact" />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}